import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Modal, Form, Spinner, Badge, 
  Alert, InputGroup 
} from 'react-bootstrap';
import { 
  FaFileUpload, FaTrash, FaEye, FaSearch, FaFilePdf,
  FaCheckCircle, FaExclamationTriangle, FaInfoCircle
} from 'react-icons/fa';
import axios from 'axios';
import '../styles/AdminDiagramas.css';

const API_URL = process.env.REACT_APP_API_URL || "/";

const AdminDiagramas = () => {
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModalCarga, setShowModalCarga] = useState(false);
  const [showModalDetalles, setShowModalDetalles] = useState(false);
  const [legajoSeleccionado, setLegajoSeleccionado] = useState(null);
  const [detallesDiagramas, setDetallesDiagramas] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [loadingCarga, setLoadingCarga] = useState(false);
  const [resultadoCarga, setResultadoCarga] = useState(null);
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    cargarResumen();
  }, []);

  const cargarResumen = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagramas/admin/resumen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setResumen(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar resumen:', error);
      alert('Error al cargar el resumen de diagramas');
    } finally {
      setLoading(false);
    }
  };

  const verDetalles = async (legajo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagramas/${legajo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDetallesDiagramas(response.data.data);
        setLegajoSeleccionado(legajo);
        setShowModalDetalles(true);
      }
    } catch (error) {
      console.error('Error al cargar detalles:', error);
      alert('Error al cargar los detalles del legajo');
    }
  };

  const eliminarDiagrama = async (diagramaId) => {
    if (!window.confirm('¿Está seguro de eliminar este diagrama? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/diagramas/${diagramaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Diagrama eliminado correctamente');
        // Recargar detalles
        verDetalles(legajoSeleccionado);
        // Recargar resumen
        cargarResumen();
      }
    } catch (error) {
      console.error('Error al eliminar diagrama:', error);
      alert('Error al eliminar el diagrama: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleArchivosChange = (e) => {
    const files = Array.from(e.target.files);
    const pdfs = files.filter(file => file.type === 'application/pdf');

    if (pdfs.length !== files.length) {
      alert('⚠️ Algunos archivos fueron ignorados por no ser PDF');
    }

    setArchivos(pdfs);
  };

  const handleSubirDiagramas = async (e) => {
    e.preventDefault();

    if (archivos.length === 0) {
      alert('Debes seleccionar al menos un archivo PDF');
      return;
    }

    const formData = new FormData();
    archivos.forEach(file => formData.append('archivos', file));
    
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    let legajoActual = '';
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        legajoActual = (user?.Legajo || user?.CdUsuario)?.toString() || '';
      } catch (err) {
        console.error('Error al parsear usuario:', err);
      }
    }
    
    formData.append('subido_por', legajoActual);

    setLoadingCarga(true);
    setResultadoCarga(null);

    try {
      const response = await axios.post(`${API_URL}/api/diagramas/carga-masiva`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setResultadoCarga(response.data);
        setArchivos([]);
        // Recargar resumen después de subir
        setTimeout(() => {
          cargarResumen();
        }, 1000);
      }
    } catch (error) {
      console.error('Error en carga masiva:', error);
      setResultadoCarga({
        success: false,
        error: error.response?.data?.error || 'Error al procesar la carga masiva'
      });
    } finally {
      setLoadingCarga(false);
    }
  };

  const handleCerrarModalCarga = () => {
    setShowModalCarga(false);
    setArchivos([]);
    setResultadoCarga(null);
  };

  const handleCerrarModalDetalles = () => {
    setShowModalDetalles(false);
    setLegajoSeleccionado(null);
    setDetallesDiagramas([]);
  };

  const abrirPDF = (nombreArchivo) => {
    // Verificar primero si el archivo existe
    const url = `${API_URL}/assets/Diagramas/${nombreArchivo}`;
    
    // Intentar abrir el PDF
    fetch(url, { method: 'HEAD' })
      .then(response => {
        if (response.ok) {
          window.open(url, '_blank');
        } else {
          alert(`❌ Archivo no encontrado: ${nombreArchivo}\n\n` +
                `Este registro existe en la base de datos pero el archivo físico no está en el servidor.\n` +
                `Puede ser que nunca se haya subido correctamente o fue eliminado.\n\n` +
                `Contacte al administrador para limpiar registros huérfanos.`);
          console.error('Archivo no encontrado:', url, 'Status:', response.status);
        }
      })
      .catch(error => {
        alert(`❌ Error al acceder al archivo: ${nombreArchivo}\n\nDetalle: ${error.message}`);
        console.error('Error al verificar archivo:', error);
      });
  };

  // Filtrar resumen
  const resumenFiltrado = resumen.filter(item => {
    const f = filtro.toLowerCase();
    return (
      item.legajo?.toString().includes(f) ||
      item.NmEmpleado?.toLowerCase().includes(f)
    );
  });

  return (
    <div className="admin-diagramas-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Gestión de Diagramas de Inspección</h3>
          <p className="text-muted mb-0">
            Administra los diagramas PDF cargados por legajo
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setShowModalCarga(true)}
          className="d-flex align-items-center gap-2"
        >
          <FaFileUpload />
          Cargar Diagramas
        </Button>
      </div>

      {/* Barra de búsqueda */}
      <Card className="mb-3">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por legajo o nombre..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      {/* Estadísticas */}
      <div className="row mb-4">
        <div className="col-md-4">
          <Card className="text-center bg-light">
            <Card.Body>
              <h2 className="text-primary">{resumen.length}</h2>
              <p className="mb-0 text-muted">Legajos con Diagramas</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="text-center bg-light">
            <Card.Body>
              <h2 className="text-success">
                {resumen.reduce((sum, item) => sum + (parseInt(item.cantidad_diagramas) || 0), 0)}
              </h2>
              <p className="mb-0 text-muted">Total de Diagramas</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="text-center bg-light">
            <Card.Body>
              <h2 className="text-info">
                {resumen.filter(item => item.cantidad_diagramas > 1).length}
              </h2>
              <p className="mb-0 text-muted">Con Múltiples Diagramas</p>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Tabla de resumen */}
      <Card>
        <Card.Header className="bg-primary text-white">
          <strong>Resumen por Legajo</strong>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando resumen...</p>
            </div>
          ) : resumenFiltrado.length === 0 ? (
            <div className="text-center py-5">
              <FaInfoCircle size={50} className="text-muted mb-3" />
              <p className="text-muted">
                {filtro ? 'No se encontraron resultados' : 'No hay diagramas cargados'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Legajo</th>
                    <th>Empleado</th>
                    <th className="text-center">Cantidad</th>
                    <th>Última Subida</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {resumenFiltrado.map((item) => (
                    <tr key={item.legajo}>
                      <td>
                        <Badge bg="secondary">{item.legajo}</Badge>
                      </td>
                      <td>
                        <strong>{item.NmEmpleado || 'Sin nombre'}</strong>
                      </td>
                      <td className="text-center">
                        <Badge 
                          bg={item.cantidad_diagramas > 1 ? 'info' : 'success'}
                          className="px-3"
                        >
                          {item.cantidad_diagramas}
                        </Badge>
                      </td>
                      <td>
                        {new Date(item.ultima_subida).toLocaleString('es-AR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => verDetalles(item.legajo)}
                        >
                          <FaEye className="me-1" />
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Carga Masiva */}
      <Modal show={showModalCarga} onHide={handleCerrarModalCarga} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileUpload className="me-2 text-primary" />
            Carga Masiva de Diagramas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FaInfoCircle className="me-2" />
            <strong>Formato esperado:</strong> Los archivos PDF deben tener el formato 
            <code className="mx-1">DYYYYMMlegajo.pdf</code> 
            (Ejemplo: <code>D2026037393.pdf</code> para legajo 7393 en marzo 2026)
          </Alert>

          <Form onSubmit={handleSubirDiagramas}>
            <Form.Group className="mb-3">
              <Form.Label>Seleccionar Archivos PDF</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                multiple
                onChange={handleArchivosChange}
                disabled={loadingCarga}
              />
              {archivos.length > 0 && (
                <div className="mt-3">
                  <strong>Archivos seleccionados ({archivos.length}):</strong>
                  <div className="mt-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {archivos.map((file, idx) => (
                      <div key={idx} className="d-flex align-items-center gap-2 mb-1">
                        <FaFilePdf className="text-danger" />
                        <small>{file.name}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.Group>

            {loadingCarga && (
              <div className="text-center py-3">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Procesando carga masiva...</p>
              </div>
            )}

            {resultadoCarga && (
              <div className="mt-3">
                {resultadoCarga.success ? (
                  <>
                    <Alert variant="success">
                      <FaCheckCircle className="me-2" />
                      <strong>Carga completada</strong>
                      <ul className="mb-0 mt-2">
                        <li>Total procesados: {resultadoCarga.total}</li>
                        <li>Registrados: {resultadoCarga.registrados}</li>
                        {resultadoCarga.duplicados > 0 && (
                          <li className="text-warning">Duplicados ignorados: {resultadoCarga.duplicados}</li>
                        )}
                        {resultadoCarga.ignorados > 0 && (
                          <li className="text-danger">Fallidos: {resultadoCarga.ignorados}</li>
                        )}
                      </ul>
                    </Alert>

                    {resultadoCarga.warnings && resultadoCarga.warnings.length > 0 && (
                      <Alert variant="warning">
                        <FaExclamationTriangle className="me-2" />
                        <strong>Advertencias:</strong>
                        <ul className="mb-0 mt-2">
                          {resultadoCarga.warnings.map((warn, idx) => (
                            <li key={idx}>
                              <strong>{warn.archivo}</strong> - {warn.motivo}
                            </li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    {resultadoCarga.errores && resultadoCarga.errores.length > 0 && (
                      <Alert variant="danger">
                        <strong>Errores:</strong>
                        <ul className="mb-0 mt-2">
                          {resultadoCarga.errores.map((err, idx) => (
                            <li key={idx}>
                              <strong>{err.archivo}</strong>: {err.error}
                            </li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert variant="danger">
                    <strong>Error en la carga:</strong> {resultadoCarga.error}
                  </Alert>
                )}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={handleCerrarModalCarga}>
                Cerrar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={archivos.length === 0 || loadingCarga}
              >
                <FaFileUpload className="me-2" />
                {loadingCarga ? 'Procesando...' : 'Subir Diagramas'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de Detalles */}
      <Modal show={showModalDetalles} onHide={handleCerrarModalDetalles} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Diagramas del Legajo: <Badge bg="primary">{legajoSeleccionado}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detallesDiagramas.length === 0 ? (
            <Alert variant="info">No hay diagramas para este legajo</Alert>
          ) : (
            <Table hover responsive>
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Archivo</th>
                  <th>Fecha Subida</th>
                  <th>Subido Por</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {detallesDiagramas.map((diagrama) => (
                  <tr key={diagrama.id}>
                    <td>{diagrama.id}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <FaFilePdf className="text-danger" />
                        <small>{diagrama.nombre_archivo}</small>
                      </div>
                    </td>
                    <td>
                      {new Date(diagrama.fecha_subida).toLocaleString('es-AR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      <Badge bg="secondary">{diagrama.subido_por}</Badge>
                    </td>
                    <td className="text-center">
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => abrirPDF(diagrama.nombre_archivo)}
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => eliminarDiagrama(diagrama.id)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarModalDetalles}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDiagramas;
